import React, { FC, useContext, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@apollo/react-hooks';
import { useHistory } from 'react-router-dom';

import { AnimatedScreen, HRule, LoadingPage } from 'Shared/components';
import { getGraphqlErrors, PartnerContext } from 'Admin/utils';
import {
  yupEinValidator,
  yupNpnValidator,
  yupPhoneValidator,
  yupZipValidator,
} from 'Admin/utils/formValidators';
import { PartnerForm, PartnerFormValues } from 'Admin/Partners/components';
import { isPresent } from 'Shared/utils';
import { getErrorMessage } from 'QuoteApp/utils'; // Todo: move this out to Shared/utils
import { CREATE_PARTNER } from 'Admin/Partners/graphql';

const partnerDetailsSchema = Yup.object().shape({
  name: Yup.string()
    .required('Required')
    .test('not-only-whitespace', 'Required', (value) => {
      return value ? value.trim().length > 0 : false;
    }),
  channel: Yup.string().required('Required'),
  commissionStructure: Yup.string().required('Required'),
  licensedAgency: Yup.object().shape({
    phoneNumber: yupPhoneValidator(),
    employerIdentificationNumber: Yup.string().when('soleProprietor', {
      is: false,
      then: yupEinValidator(),
    }),
    nationalProducerNumber: Yup.string().when('soleProprietor', {
      is: false,
      then: yupNpnValidator().required('Required'),
    }),
    soleProprietor: Yup.boolean(),
  }),
  address: Yup.object().shape({
    lineOne: Yup.string(),
    lineTwo: Yup.string(),
    city: Yup.string(),
    state: Yup.string(),
    zipCode: yupZipValidator(),
  }),
  sourceOfSale: Yup.string(),
});

type CreatePartnerPayload = {
  partnerCreate: {
    partner: {
      id: string;
      name: string;
      channel: string;
      commissionStructure: string;
      phoneNumber: string;
      soleProprietor: boolean;
      employerIdentificationNumber: string;
      nationalProducerNumber: string;
      address: {
        lineOne: string;
        lineTwo: string;
        city: string;
        zipCode: string;
        state: string;
      };
    };
  };
};

export const CreatePartnerDetails: FC = () => {
  const history = useHistory();
  const { loading, currentPartner } = useContext(PartnerContext);
  const [error, setError] = useState<string>();
  const header = isPresent(currentPartner)
    ? `Create an Agency of ${currentPartner.name}`
    : 'Create Agency';

  const [createPartner] = useMutation<
    CreatePartnerPayload,
    {
      name: string;
      channel: string;
      commissionStructure: string;
      sourceOfSale: string;
      soleProprietor: boolean;
      nationalProducerNumber: string;
      employerIdentificationNumber: string;
      phoneNumber: string;
      lineOne: string;
      lineTwo: string;
      city: string;
      zip: string;
      state: string;
    }
  >(CREATE_PARTNER, { errorPolicy: 'ignore' });

  if (loading) return <LoadingPage />;

  if (!currentPartner) {
    return null;
  }

  return (
    <AnimatedScreen testId="create-partner-details">
      <h1>{header}</h1>
      <HRule size="lg" />
      <Formik<PartnerFormValues>
        initialValues={{
          name: '',
          principalAgent: '',
          channel: '',
          commissionStructure: '',
          sourceOfSale: '',
          licensedAgency: {
            phoneNumber: '',
            employerIdentificationNumber: '',
            nationalProducerNumber: '',
            soleProprietor: false,
          },
          address: {
            lineOne: '',
            lineTwo: '',
            city: '',
            zipCode: '',
            state: '',
          },
          canQuotePortal: false,
          npnStatus: 'not_applicable',
          productOffering: {
            nonStandardAuto: false,
            standardAuto: false,
          },
        }}
        validationSchema={partnerDetailsSchema}
        validateOnBlur={false}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          const createParams = isPresent(currentPartner)
            ? { parentId: currentPartner.id }
            : {};
          try {
            const { data } = await createPartner({
              variables: {
                ...createParams,
                name: values.name,
                channel: values.channel,
                commissionStructure: values.commissionStructure,
                sourceOfSale: values.sourceOfSale,
                soleProprietor: values.licensedAgency.soleProprietor,
                nationalProducerNumber:
                  values.licensedAgency.nationalProducerNumber,
                employerIdentificationNumber:
                  values.licensedAgency.employerIdentificationNumber,
                phoneNumber: values.licensedAgency.phoneNumber,
                lineOne: values.address.lineOne,
                lineTwo: values.address.lineTwo,
                city: values.address.city,
                state: values.address.state,
                zip: values.address.zipCode,
              },
            });
            resetForm();
            const partnerId = data?.partnerCreate.partner.id;
            const partnerIdUrl = `/admin/partners/${partnerId}/details`;
            history.push(partnerIdUrl);
          } catch (err) {
            const graphqlError = getGraphqlErrors(err);
            setError(graphqlError || getErrorMessage(err));
          } finally {
            setSubmitting(false);
          }
        }}
      >
        <PartnerForm type="create" footerError={error} />
      </Formik>
    </AnimatedScreen>
  );
};
